/**
 * B2: First-login persona question.
 * Full-screen overlay shown when persona is not set.
 * Two choices: Operator (business owner) or Buyer (customer).
 */
import { useTranslation } from "@/i18n";
import { usePersona, type Persona } from "@/providers/persona-context";
import { cn } from "@/lib/utils";

interface PersonaOptionProps {
  persona: Persona;
  icon: string;
  titleKey: string;
  descKey: string;
  selected: boolean;
  onSelect: () => void;
}

function PersonaOption({ icon, titleKey, descKey, selected, onSelect }: PersonaOptionProps) {
  const { t } = useTranslation();

  return (
    <button
      onClick={onSelect}
      className={cn(
        "flex flex-col items-center gap-3 rounded-xl border-2 p-8 text-center transition-all hover:shadow-lg",
        selected
          ? "border-primary bg-primary/5 shadow-md"
          : "border-border bg-card hover:border-primary/40",
      )}
    >
      <span className="text-4xl">{icon}</span>
      <h3 className="text-lg font-semibold text-foreground">{t(titleKey)}</h3>
      <p className="text-sm text-muted-foreground">{t(descKey)}</p>
    </button>
  );
}

export function PersonaPrompt() {
  const { t } = useTranslation();
  const { persona, setPersona, isPersonaSet } = usePersona();

  if (isPersonaSet) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
      <div className="mx-auto max-w-2xl px-6 text-center">
        <h1 className="mb-2 text-3xl font-bold text-foreground">
          {t("persona.prompt.title")}
        </h1>
        <p className="mb-8 text-muted-foreground">
          {t("persona.prompt.subtitle")}
        </p>

        <div className="grid gap-6 sm:grid-cols-2">
          <PersonaOption
            persona="operator"
            icon="🏢"
            titleKey="persona.prompt.operator"
            descKey="persona.prompt.operatorDesc"
            selected={persona === "operator"}
            onSelect={() => setPersona("operator")}
          />
          <PersonaOption
            persona="buyer"
            icon="🛒"
            titleKey="persona.prompt.buyer"
            descKey="persona.prompt.buyerDesc"
            selected={persona === "buyer"}
            onSelect={() => setPersona("buyer")}
          />
        </div>
      </div>
    </div>
  );
}
